/* eslint-disable brace-style */
import type { ModalService } from './../ricos-modals';
import type {
  CustomizableKeyboardShortcut,
  EventSource,
  HotKeysProps,
  KeyboardShortcut,
  LocalizedDisplayData,
  Platform,
  PublicCommands,
  PublisherProvider,
  TFunction,
  TiptapEditor,
} from './../ricos-types';

import type { Shortcut } from './editor-keyboard-shortcut';
import { EditorKeyboardShortcut } from './editor-keyboard-shortcut';

class ShortcutCollisionError extends Error {}

const TOPICS: ['ricos.shortcuts.functionality.applied'] = ['ricos.shortcuts.functionality.applied'];

export type Shortcuts = EditorKeyboardShortcuts;

export class EditorKeyboardShortcuts
  implements EventSource<['ricos.shortcuts.functionality.applied']>
{
  private shortcuts: Shortcut[] = [];

  isDebugMode = false;

  readonly topicsToPublish = TOPICS;

  publishers!: PublisherProvider<['ricos.shortcuts.functionality.applied']>;

  private readonly modalService: ModalService;

  constructor(
    modalService: ModalService,
    config: { isDebugMode: boolean } = { isDebugMode: false }
  ) {
    this.modalService = modalService;
    this.isDebugMode = config.isDebugMode;
  }

  private hasDuplicate(shortcut: Shortcut) {
    return this.shortcuts.find(s => s.equals(shortcut));
  }

  /**
   * Constructs `react-hotkeys` compatible data for given group
   */
  getHotKeysProps({
    group,
    publicCommands,
    editor,
    t,
    platform,
  }: {
    group: string;
    publicCommands: PublicCommands;
    editor: TiptapEditor;
    t: TFunction;
    platform: Platform;
  }): HotKeysProps {
    const publisher = this.publishers.byTopic('ricos.shortcuts.functionality.applied');
    return this.shortcuts
      .filter(s => s.isEnabled())
      .filter(
        s =>
          s.getGroup() === group ||
          [
            'ShortcutsSheet_Section_General',
            'ShortcutsSheet_Section_BlocksMenu',
            'ShortcutsSheet_Section_ToolbarNavigation',
          ].includes(s.getGroup())
      )
      .reduce(
        ({ keyMap, handlers }, shortcut) => {
          return {
            allowChanges: false,
            keyMap: {
              ...keyMap,
              [shortcut.getName()]: {
                sequences: [shortcut.getKeys(platform).toString().toLowerCase()],
                ...shortcut.getDisplayData(t, platform),
              },
            },
            handlers: {
              ...handlers,
              [shortcut.getName()]: (event?: KeyboardEvent) => {
                shortcut.getCommand()({
                  publicCommands,
                  modalService: this.modalService,
                  event,
                  editor,
                });
                publisher.publish({ shortcutName: shortcut.getName() });
              },
            },
          };
        },
        { keyMap: {}, handlers: {}, allowChanges: false }
      );
  }

  /**
   * Registers shortcut, validates it has no conflicts.
   */
  register(shortcut: KeyboardShortcut): void {
    const candidate = EditorKeyboardShortcut.of(shortcut);

    // TODO: validate vs browser built-in shortcuts
    const duplicate = this.hasDuplicate(candidate);
    if (duplicate) {
      throw new ShortcutCollisionError(
        `the shortcut ${candidate.getName()} conflicts with ${duplicate.getName()}`
      );
    }

    this.shortcuts.push(candidate);
  }

  /**
   * Registers shortcut, override in case of a conflicts.
   */
  mergeKeyboadShortcutProp(shortcut: CustomizableKeyboardShortcut): void {
    const candidate = EditorKeyboardShortcut.of(shortcut);

    // TODO: validate vs browser built-in shortcuts
    const duplicate = this.hasDuplicate(candidate);
    if (duplicate) {
      this.shortcuts = this.shortcuts.filter(s => !s.equals(duplicate));
    }

    this.shortcuts.push(candidate);
  }

  /**
   * Unregisters shortcut
   */
  unregister(shortcut: KeyboardShortcut): void {
    this.shortcuts = this.shortcuts.filter(s => !s.equals(EditorKeyboardShortcut.of(shortcut)));
  }

  /**
   * Filters shortcuts according to predicate
   */
  filter(predicate: (shortcut: Shortcut) => boolean): Shortcuts {
    this.shortcuts = this.shortcuts.filter(predicate);
    return this;
  }

  /**
   * Gets shortcut array
   */
  asArray(): Shortcut[] {
    return this.shortcuts;
  }

  /**
   * Provides shortcut display data by given shortcut name for current locale and platform
   */
  getShortcutDisplayData(name: string, t: TFunction, platform: Platform): LocalizedDisplayData {
    const shortcut = this.shortcuts.find(s => s.getName() === name);

    if (!shortcut) {
      return {
        name: 'Empty',
        description: '',
        keyCombinationText: '',
        tooltipHint: '',
        group: '',
      };
    }

    return shortcut.getDisplayData(t, platform);
  }

  /**
   * Builds a map of shortcut display data, grouped by shortcut groups
   */
  getDisplayData(t: TFunction, platform: Platform): { [group: string]: LocalizedDisplayData[] } {
    return this.shortcuts.reduce(
      (map, shortcut) => ({
        ...map,
        [shortcut.getGroup()]: [
          ...(map[shortcut.getGroup()] || []),
          shortcut.getDisplayData(t, platform),
        ],
      }),
      {} as { [group: string]: LocalizedDisplayData[] }
    );
  }
}
